import React, { useRef, useState, DragEvent, ChangeEvent } from 'react';
import { validateImages } from './imageUtils';

interface ImageUploadProps {
  onFilesSelected: (files: File[]) => void;
  maxImages?: number;
  currentCount?: number;
  disabled?: boolean;
  className?: string;
}

export const ImageUpload: React.FC<ImageUploadProps> = ({
  onFilesSelected,
  maxImages = 10,
  currentCount = 0,
  disabled = false,
  className = '',
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const remaining = maxImages - currentCount;
  const canUpload = remaining > 0 && !disabled;

  const handleFiles = (files: FileList | null) => {
    if (!files || files.length === 0) return;

    setError(null);

    const fileArray = Array.from(files);
    const { valid, errors } = validateImages(fileArray, currentCount);

    if (errors.length > 0) {
      setError(errors[0].message);
      setTimeout(() => setError(null), 5000); // Clear error after 5s
      return;
    }

    if (valid.length > 0) {
      onFilesSelected(valid);
    }
  };

  const handleClick = () => {
    if (canUpload) {
      fileInputRef.current?.click();
    }
  };

  const handleFileInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    handleFiles(e.target.files);
    // Reset input so same file can be selected again
    e.target.value = '';
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (canUpload) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (!canUpload) return;

    handleFiles(e.dataTransfer.files);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick();
    }
  };

  return (
    <div className={className}>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/jpg,image/png,image/webp,image/gif,video/mp4"
        multiple
        onChange={handleFileInputChange}
        style={{ display: 'none' }}
        aria-label="Upload images or videos"
        disabled={!canUpload}
      />

      <div
        onClick={handleClick}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onKeyDown={handleKeyDown}
        role="button"
        tabIndex={canUpload ? 0 : -1}
        aria-label={`Upload images or videos. ${remaining} of ${maxImages} remaining. Click to select or drag and drop. Supports JPG, PNG, WebP, GIF (10MB) and MP4 video (30MB)`}
        aria-disabled={!canUpload}
        className={`
          relative
          border-2 border-dashed rounded-lg
          p-8
          text-center
          transition-all duration-200
          ${canUpload ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'}
          ${isDragging && canUpload ? 'border-balloon bg-balloon/5 scale-[1.02]' : 'border-gray-300'}
          ${!isDragging && canUpload ? 'hover:border-balloon hover:bg-gray-50' : ''}
          focus:outline-none focus:ring-2 focus:ring-balloon focus:ring-offset-2
        `}
      >
        <div className="flex flex-col items-center gap-3">
          {/* Upload Icon */}
          <div className={`text-4xl ${isDragging ? 'scale-110' : ''} transition-transform`}>
            {isDragging ? '📥' : '📷'}
          </div>

          {/* Text */}
          <div>
            <p className="text-lg font-medium text-gray-700">
              {isDragging ? 'Drop files here' : 'Upload Images & Videos'}
            </p>
            <p className="text-sm text-gray-500 mt-1">
              {canUpload ? (
                <>
                  Click to browse or drag and drop
                  <br />
                  JPG, PNG, WebP, GIF (max 10MB) · MP4 video (max 30MB)
                </>
              ) : (
                `Maximum ${maxImages} images reached`
              )}
            </p>
          </div>

          {/* Remaining count */}
          {canUpload && currentCount > 0 && (
            <p className="text-xs text-gray-400">
              {remaining} {remaining === 1 ? 'slot' : 'slots'} remaining
            </p>
          )}
        </div>

        {/* Error message */}
        {error && (
          <div
            role="alert"
            aria-live="polite"
            className="absolute bottom-0 left-0 right-0 bg-red-50 border-t-2 border-red-200 p-2 text-sm text-red-600"
          >
            ⚠️ {error}
          </div>
        )}
      </div>
    </div>
  );
};
